/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, {
  useState, useRef, useEffect, useCallback, memo,
} from 'react';
import { Modal, InputNumber } from 'antd';
import { fabric } from 'fabric';
import './map-scaler-modal.less';
import { IOrigin, IPixelsPerUnit } from './mapScalerModal.types';

type MapScalerModalProps = {
  image: Blob;
  visible: boolean;
  units: string;
  onCancel: () => void;
  onSave: ({ origin, pixelsPerUnit }:
  { origin: IOrigin; pixelsPerUnit: IPixelsPerUnit; }
  ) => void;
  initialOrigin?: IOrigin | null;
  initialPixelsPerUnit?: IPixelsPerUnit | null;
};

const MapScalerModal = ({
  image,
  visible,
  units,
  onCancel,
  onSave,
  initialOrigin = null,
  initialPixelsPerUnit = null,
}: MapScalerModalProps): JSX.Element => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const [canvas, setCanvas] = useState<fabric.Canvas | null>(null);
  const [imgDimensions, setImgDimensions] = useState<{ width: number; height: number} | null>(null);
  const [origin, setOrigin] = useState<IOrigin | null>(null);
  const [isSelectingOrigin, setIsSelectingOrigin] = useState<boolean>(false);
  const [tempOriginCircle, setTempOriginCircle] = useState<fabric.Circle | null>(null);
  const [isSelectingScalePoints, setIsSelectingScalePoints] = useState<boolean>(false);
  const [tempScaleCircles, setTempScaleCircles] = useState<fabric.Circle[] | []>([]);
  const [scaleDistance, setScaleDistance] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [pixelsPerUnit, setPixelsPerUnit] = useState<IPixelsPerUnit | null>(null);
  const [isPanning, setIsPanning] = useState<boolean>(false);
  const lastMousePosition = useRef({ x: 0, y: 0 });
  const hydratedRef = useRef(false);
  const [pointerCoords, setPointerCoords] = useState<{ x: string; y: string;} | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number;} | null>(null);

  // `0` is a valid offset (origin picked on the image edge), so compare against
  // null instead of relying on truthiness.
  const saveEnabled = origin !== null && pixelsPerUnit !== null && pixelsPerUnit > 0;
  const isSelectMode = isSelectingOrigin || isSelectingScalePoints;

  const clearOriginHandler = () => {
    if (!canvas) return;

    setOrigin(null);
    setIsSelectingOrigin(false);
    setTempOriginCircle((prev) => {
      if (prev) {
        canvas.remove(prev);
        canvas.renderAll();
      }
      return null;
    });
  };

  const setOriginHandler = () => {
    if (tempOriginCircle) {
      // eslint-disable-next-line no-alert
      alert('Origin is already selected. Delete it first to select again.');
      return;
    }
    setIsSelectingOrigin(true);
  };

  const clearScalePointsHandler = () => {
    if (!canvas) return;

    setIsSelectingScalePoints(false);
    setPixelsPerUnit(null);
    setTempScaleCircles((prev) => {
      prev?.forEach((circle) => {
        canvas.remove(circle);
      });
      canvas.renderAll();
      return [];
    });
  };

  const setScalePointsHandler = () => {
    if (tempScaleCircles && tempScaleCircles.length) {
      // eslint-disable-next-line no-alert
      alert('Scale points are already selected. Delete them first to select again.');
      return;
    }
    setIsSelectingScalePoints(true);
  };

  const handleMouseClick = useCallback((e: fabric.IEvent) => {
    if (!canvas || !imgDimensions) return;
    if (!isSelectingOrigin && !isSelectingScalePoints) return;
    // Two points is the maximum; a stray extra click would otherwise leave the
    // scale uncomputable (and the Done button disabled) with no visible reason.
    if (isSelectingScalePoints && tempScaleCircles.length >= 2) return;

    const pointer = canvas.getPointer(e.e);
    const { x, y } = pointer;

    const scaleX = imgDimensions.width / canvas.width!;
    const scaleY = imgDimensions.height / canvas.height!;

    const imageX = x * scaleX;
    const imageY = y * scaleY;

    const zoom = canvas.getZoom();

    const circle = new fabric.Circle({
      left: x,
      top: y,
      radius: 5 / zoom,
      fill: 'red',
      originX: 'center',
      originY: 'center',
      selectable: false,
      evented: false,
    });
    canvas.add(circle);
    canvas.renderAll();

    if (isSelectingOrigin) {
      const xofffset = imageX;
      const yoffset = imgDimensions.height - imageY;

      setOrigin({ xofffset: Number(xofffset.toFixed(1)), yoffset: Number(yoffset.toFixed(1)) });

      setIsSelectingOrigin(false);
      setTempOriginCircle(circle);
    } else if (isSelectingScalePoints) {
      setTempScaleCircles((prev) => {
        if (prev.length === 1) setIsSelectingScalePoints(false);
        return [...prev, circle];
      });
    }
  }, [isSelectingOrigin, canvas, imgDimensions, isSelectingScalePoints, tempScaleCircles]);

  const modalCancelHandler = () => {
    // antd flags the modal wrapper aria-hidden while it closes; leaving focus on
    // a control inside it makes Chrome log an aria-hidden warning.
    (document.activeElement as HTMLElement | null)?.blur();
    onCancel();
    setOrigin(null);
    setTempOriginCircle(null);
    setTempScaleCircles([]);
    setScaleDistance({ x: 0, y: 0 });
    setPixelsPerUnit(null);
    setIsSelectingOrigin(false);
    setIsSelectingScalePoints(false);
  };

  const resetZoomHandler = () => {
    if (!canvas) return;

    canvas.setZoom(1);
    canvas.viewportTransform = [1, 0, 0, 1, 0, 0];
    canvas.requestRenderAll();
  };

  const modalSaveHandler = () => {
    if (!origin || pixelsPerUnit === null) return;

    onSave({ origin, pixelsPerUnit });
    modalCancelHandler();
  };

  const handleMouseDown = useCallback((opt: fabric.IEvent) => {
    if (!canvas || isSelectingOrigin || isSelectingScalePoints) return;

    const e = opt.e as WheelEvent;
    setIsPanning(true);
    lastMousePosition.current = { x: e.clientX, y: e.clientY };
    canvas.selection = false;
    canvas.defaultCursor = 'grab';
  }, [canvas, isSelectingOrigin, isSelectingScalePoints]);

  const handleZoom = useCallback((opt: fabric.IEvent) => {
    if (!canvas) return;

    const e = opt.e as WheelEvent;
    e.preventDefault();
    e.stopPropagation();

    const isPinchZoom = e.ctrlKey || e.metaKey;
    let zoom = canvas.getZoom();

    if (isPinchZoom) {
      zoom *= 0.999 ** e.deltaY;
    } else {
      zoom *= 1.1 ** (-e.deltaY / 100);
    }

    zoom = Math.min(Math.max(0.01, zoom), 20);
    const point = new fabric.Point(e.offsetX, e.offsetY);
    canvas.zoomToPoint(point, zoom);
  }, [canvas]);

  const handleMouseMove = useCallback((opt: fabric.IEvent) => {
    if (!canvas || !isPanning) return;

    const e = opt.e as WheelEvent;
    const vpt = canvas.viewportTransform;

    vpt![4] += e.clientX - lastMousePosition.current.x;
    vpt![5] += e.clientY - lastMousePosition.current.y;

    canvas.requestRenderAll();

    lastMousePosition.current = { x: e.clientX, y: e.clientY };
  }, [canvas, isPanning]);

  const handleMouseUp = useCallback(() => {
    if (!canvas || !isPanning) return;

    if (canvas.viewportTransform) {
      canvas.setViewportTransform(canvas.viewportTransform);
    }

    setIsPanning(false);
    canvas.selection = true;
    const isSelecting = isSelectingOrigin || isSelectingScalePoints;
    canvas.defaultCursor = isSelecting ? 'crosshair' : 'default';
  }, [canvas, isPanning, isSelectingOrigin, isSelectingScalePoints]);

  const handlePointerMove = useCallback((opt: fabric.IEvent) => {
    if (!canvas || !imgDimensions || !origin || !pixelsPerUnit) {
      setTooltipPosition(null);
      setPointerCoords(null);
      return;
    }

    const pointer = canvas.getPointer(opt.e);

    // Scale from canvas coordinates to original image coordinates
    const scaleX = imgDimensions!.width / canvas.width!;
    const scaleY = imgDimensions!.height / canvas.height!;

    const imageX = pointer.x * scaleX;
    const imageY = pointer.y * scaleY;

    const worldX = (imageX - origin.xofffset) / pixelsPerUnit;
    const worldY = ((imgDimensions!.height - imageY) - origin.yoffset) / pixelsPerUnit;

    const e = opt.e as MouseEvent;
    const rect = canvasRef.current?.getBoundingClientRect();
    if (rect) {
      setTooltipPosition({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }

    setPointerCoords({
      x: worldX.toFixed(2),
      y: worldY.toFixed(2),
    });
  }, [canvas, origin, pixelsPerUnit, imgDimensions]);

  useEffect(() => {
    if (!visible || !image) return undefined;

    const imageUrl = URL.createObjectURL(image);
    let fabricCanvas: fabric.Canvas | null = null;
    let cancelled = false;

    const img = new Image();

    img.onload = () => {
      if (cancelled || !canvasRef.current) return;

      const containerWidth = canvasContainerRef.current?.clientWidth || 800;
      const scale = containerWidth / img.width;
      const height = img.height * scale;
      setImgDimensions({ width: img.width, height: img.height });

      const parentContainer = canvasContainerRef.current?.parentElement;
      if (parentContainer) {
        parentContainer.style.height = `${height}px`;
      }

      fabricCanvas = new fabric.Canvas(canvasRef.current, {
        width: containerWidth,
        height,
        selection: false,
      });

      fabric.Image.fromURL(imageUrl, (fabricImage) => {
        if (cancelled || !fabricCanvas) return;

        const imgScale = containerWidth / fabricImage.width!;
        fabricImage.set({
          scaleX: imgScale,
          scaleY: imgScale,
          selectable: false,
          evented: false,
          stroke: '#000000',
          strokeWidth: 1,
        });
        fabricCanvas.setBackgroundImage(fabricImage, fabricCanvas.renderAll.bind(fabricCanvas));
      });

      setCanvas(fabricCanvas);
    };

    img.src = imageUrl;

    return () => {
      cancelled = true;
      setCanvas(null);
      setImgDimensions(null);
      // fabric wraps the <canvas> in its own container and stacks an extra
      // "upper canvas" on top to capture events. Without dispose() the next
      // open wraps the same element again and the stale upper canvas — still
      // the topmost node — swallows every click, so nothing can be selected.
      fabricCanvas?.dispose();
      URL.revokeObjectURL(imageUrl);
    };
  }, [visible, image]);

  // Prefill the scaler with the values already stored on the facility.
  useEffect(() => {
    if (!canvas || !imgDimensions) {
      hydratedRef.current = false;
      return;
    }
    if (hydratedRef.current) return;
    hydratedRef.current = true;

    if (initialPixelsPerUnit) setPixelsPerUnit(initialPixelsPerUnit);
    if (!initialOrigin) return;

    const scaleX = canvas.width! / imgDimensions.width;
    const scaleY = canvas.height! / imgDimensions.height;

    const circle = new fabric.Circle({
      left: initialOrigin.xofffset * scaleX,
      top: (imgDimensions.height - initialOrigin.yoffset) * scaleY,
      radius: 5 / canvas.getZoom(),
      fill: 'red',
      originX: 'center',
      originY: 'center',
      selectable: false,
      evented: false,
    });
    canvas.add(circle);
    canvas.renderAll();

    setOrigin(initialOrigin);
    setTempOriginCircle(circle);
  }, [canvas, imgDimensions, initialOrigin, initialPixelsPerUnit]);

  useEffect(() => {
    if (canvas) {
      const isSelecting = isSelectingOrigin || isSelectingScalePoints;
      canvas.hoverCursor = isSelecting ? 'crosshair' : 'default';
      canvas.defaultCursor = isSelecting ? 'crosshair' : 'default';
      canvas.renderAll();
    }
  }, [canvas, isSelectingOrigin, isSelectingScalePoints]);

  useEffect(() => {
    if (!canvas || !imgDimensions) return;

    canvas.on('mouse:down', handleMouseClick);

    // eslint-disable-next-line consistent-return
    return () => {
      canvas.off('mouse:down', handleMouseClick);
    };
  }, [canvas, imgDimensions, handleMouseClick]);

  useEffect(() => {
    if (!canvas || tempScaleCircles.length !== 2) return;
    if (scaleDistance.x === 0 && scaleDistance.y === 0) {
      setPixelsPerUnit(null);
      return;
    }

    const [circle1, circle2] = tempScaleCircles;

    // Calculate pixel distance in CANVAS coordinates
    const dxCanvas = Math.abs(circle2.left! - circle1.left!);
    const dyCanvas = Math.abs(circle2.top! - circle1.top!);

    // Scale back to ORIGINAL IMAGE coordinates
    const scaleX = imgDimensions!.width / canvas.width!;
    const scaleY = imgDimensions!.height / canvas.height!;

    const dxImage = dxCanvas * scaleX;
    const dyImage = dyCanvas * scaleY;

    // Calculate pixel distance in original image
    const pixelDistance = Math.sqrt(dxImage ** 2 + dyImage ** 2);

    // Calculate real distance
    const realDistance = Math.sqrt(scaleDistance.x ** 2 + scaleDistance.y ** 2);

    if (realDistance > 0) {
      const pxPerUnit = pixelDistance / realDistance;
      setPixelsPerUnit(Number(pxPerUnit.toFixed(3)));
    }
  }, [canvas, tempScaleCircles, scaleDistance, imgDimensions]);

  // Zoom and Pan listeners
  useEffect(() => {
    if (!canvas) return;

    canvas.on('mouse:wheel', handleZoom);
    canvas.on('mouse:down', handleMouseDown);
    canvas.on('mouse:move', handleMouseMove);
    canvas.on('mouse:up', handleMouseUp);
    canvas.on('mouse:out', handleMouseUp);

    // eslint-disable-next-line consistent-return
    return () => {
      canvas.off('mouse:wheel', handleZoom);
      canvas.off('mouse:down', handleMouseDown);
      canvas.off('mouse:move', handleMouseMove);
      canvas.off('mouse:up', handleMouseUp);
      canvas.off('mouse:out', handleMouseUp);
    };
  }, [canvas, handleZoom, handleMouseDown, handleMouseMove, handleMouseUp]);

  // Reverse zoom zone lines and circles
  useEffect(() => {
    if (!canvas) return;

    const handleZoomed = () => {
      const zoom = canvas.getZoom();
      tempOriginCircle?.set({ radius: 5 / zoom });
      tempScaleCircles.forEach((circle) => circle.set({ radius: 5 / zoom }));
      canvas.requestRenderAll();
    };

    canvas.on('after:render', handleZoomed);

    // eslint-disable-next-line consistent-return
    return () => {
      canvas.off('after:render', handleZoomed);
    };
  }, [canvas, tempOriginCircle, tempScaleCircles]);

  useEffect(() => {
    if (!canvas) return;
    canvas.on('mouse:move', handlePointerMove);

    // eslint-disable-next-line consistent-return
    return () => {
      canvas.off('mouse:move', handlePointerMove);
    };
  }, [canvas, handlePointerMove]);

  return (
    <Modal
      title={null}
      closable={false}
      centered={false}
      open={visible}
      wrapClassName="map-scale-wrapper"
      okText="Done"
      onOk={modalSaveHandler}
      onCancel={modalCancelHandler}
      okButtonProps={{
        disabled: !saveEnabled,
      }}
    >
      <button onClick={modalCancelHandler} type="button" className="button__close">
        Close
      </button>

      <div className="map-scale-content">
        <div className="map-scale-container">
          <div className="map-scaler-canvas-wrapper" ref={canvasContainerRef}>
            <canvas ref={canvasRef} className="map-scale-canvas" />
            { pointerCoords && tooltipPosition && (
              <div
                className="map-scale-tooltip"
                style={{
                  position: 'absolute',
                  left: `${tooltipPosition.x + 15}px`,
                  top: `${tooltipPosition.y + 15}px`,
                  background: 'rgba(0, 0, 0, 0.8)',
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  pointerEvents: 'none',
                  zIndex: 1000,
                  whiteSpace: 'nowrap',
                }}
              >
                X:
                {' '}
                {pointerCoords.x}
                {' '}
                {units}
                {' '}
                | Y:
                {' '}
                {pointerCoords.y}
                {' '}
                {units}
              </div>
            )}
          </div>
          <div className="map-scale-controls">
            <div className="map-scale-col">
              <div className="map-scale-row">
                <button
                  type="button"
                  onClick={setOriginHandler}
                  className="map-scale-select-origin-button"
                  style={{
                    cursor: isSelectMode ? 'not-allowed' : 'pointer',
                  }}
                  disabled={isSelectMode}
                >
                  {tempOriginCircle ? '✓ Selected' : 'Select origin'}
                </button>
                <button
                  type="button"
                  onClick={clearOriginHandler}
                  className="map-scale-clear-origin-button"
                  style={{
                    cursor: (isSelectMode || !tempOriginCircle) ? 'not-allowed' : 'pointer',
                  }}
                  disabled={isSelectMode || !tempOriginCircle}
                >
                  Clear
                </button>
              </div>
              <div className="map-scale-col">
                <span>
                  Xoffset:
                  {origin ? ` ${origin.xofffset} px` : ' --'}
                </span>
                <span>
                  Yoffset:
                  {origin ? ` ${origin.yoffset} px` : ' --'}
                </span>
              </div>
              {isSelectingOrigin && (
                <div>
                  <div className="map-scale-overlay-box">
                    <div>
                      Selecting origin
                    </div>
                    <div style={{ padding: '10px 0' }}>
                      Click on image to add origin point.
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsSelectingOrigin(false)}
                      className="map-scale-cancel-button"
                    >
                      Cancel selecting origin
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="map-scale-col">
              <div className="map-scale-row">
                <button
                  type="button"
                  onClick={setScalePointsHandler}
                  className="map-scale-select-origin-button"
                  style={{
                    cursor: isSelectMode ? 'not-allowed' : 'pointer',
                  }}
                  disabled={isSelectMode}
                >
                  {tempScaleCircles?.length ? '✓ Selected' : 'Select scale points'}
                </button>
                <button
                  type="button"
                  onClick={clearScalePointsHandler}
                  className="map-scale-clear-origin-button"
                  style={{
                    cursor: (isSelectMode || !tempScaleCircles.length) ? 'not-allowed' : 'pointer',
                  }}
                  disabled={isSelectMode || !tempScaleCircles.length}
                >
                  Clear
                </button>
              </div>
              <div className="map-scale-col">
                <div className="map-scale-row">
                  X distance:
                  <InputNumber
                    step={1}
                    className="map-scale-input"
                    value={scaleDistance.x}
                    onChange={(val) => {
                      if (typeof val === 'number') {
                        setScaleDistance((prev) => ({ ...prev, x: val }));
                      }
                    }}
                  />
                  <span>{ units }</span>
                </div>
                <div className="map-scale-row">
                  Y distance:
                  <InputNumber
                    step={1}
                    className="map-scale-input"
                    value={scaleDistance.y}
                    onChange={(val) => {
                      if (typeof val === 'number') {
                        setScaleDistance((prev) => ({ ...prev, y: val }));
                      }
                    }}
                  />
                  <span>{ units }</span>
                </div>
              </div>
              <div className="map-scale-col">
                <span>
                  Pixels per
                  {' '}
                  {units}
                  :
                  {' '}
                  {pixelsPerUnit ?? '--'}
                </span>
              </div>
              {isSelectingScalePoints && (
                <div>
                  <div className="map-scale-overlay-box">
                    <div>
                      Selecting scale points
                    </div>
                    <div style={{ padding: '10px 0' }}>
                      Click on image to add two scale points.
                      Enter distance between them in provided inputs.
                    </div>
                    <button
                      type="button"
                      onClick={clearScalePointsHandler}
                      className="map-scale-cancel-button"
                    >
                      Cancel selecting scale points
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="map-scale-col">
              <button
                type="button"
                onClick={resetZoomHandler}
                className="map-scale-clear-origin-button"
                style={{
                  cursor: isSelectMode ? 'not-allowed' : 'pointer',
                }}
                disabled={isSelectMode}
              >
                Reset zoom
              </button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

MapScalerModal.displayName = 'MapScalerModal';

export default memo(MapScalerModal);
